import { Type, type Static } from '@sinclair/typebox';
import { SeoBriefVoiceSchema, SeoContentType, SeoTargetKind } from './seo-brief.schema';

/**
 * Profil d'une cible de publication (plan §4.1). Construit par l'orchestration
 * (Payload pour Propelsia, backend pour un pro) et passé au moteur, qui ne
 * connaît ni Payload ni `pro_news`.
 */

/** Une page liable de la cible (maillage interne, couverture des sujets). */
export const SeoPageIndexEntrySchema = Type.Object({
  url: Type.String(),
  title: Type.String(),
  /** H1 ou titre principal si différent du `title`. */
  h1: Type.Optional(Type.String()),
  /** Résumé court (excerpt, meta description ou premiers mots), pour choisir les liens. */
  summary: Type.Optional(Type.String()),
  /** `page`, `article`, `prestation`, `lieu`, `landing`… libre, sert au tri. */
  kind: Type.Optional(Type.String()),
  /** Mot-clé principal déjà ciblé par la page, si connu (évite la cannibalisation). */
  focus_keyword: Type.Optional(Type.String()),
  /** Page de conversion (réserver, tarifs, prestation) : privilégiée pour les intentions transactionnelles. */
  is_conversion: Type.Optional(Type.Boolean()),
  published_at: Type.Optional(Type.String()),
});
export type SeoPageIndexEntry = Static<typeof SeoPageIndexEntrySchema>;

/**
 * Fait de marque ou d'établissement (offre, prix catalogue, politique d'annulation). Le brief n'en
 * retient que les faits PERTINENTS pour le sujet (`selectRelevantFacts`, 2026-10-08) : tout le
 * catalogue dans chaque article noyait le rédacteur et poussait des prix hors sujet.
 */
export const SeoProfileFactSchema = Type.Object({
  statement: Type.String(),
  source_title: Type.Optional(Type.String()),
  source_url: Type.Optional(Type.String()),
  /** Sujets du fait (nom et catégorie de la prestation, thème) ; absent = la phrase elle-même. */
  topics: Type.Optional(Type.Array(Type.String())),
});
export type SeoProfileFact = Static<typeof SeoProfileFactSchema>;

export const SeoTargetProfileSchema = Type.Object({
  target_kind: Type.Enum(SeoTargetKind),
  professional_id: Type.Optional(Type.Integer()),
  /** Hôte canonique du site (sans protocole), ex. `propelsia.ai`, `monsalon.fr`. */
  site_host: Type.String(),
  /** Hôtes considérés internes (hôte canonique + alias). */
  internal_hosts: Type.Array(Type.String()),
  voice: SeoBriefVoiceSchema,
  /** Univers de mots-clés : activités, prestations, thèmes produit. */
  seeds: Type.Array(Type.String()),
  /** Zones géographiques déclarées (villes, départements, régions) si le SEO est local : contexte des prompts. */
  areas: Type.Array(Type.String()),
  /**
   * Villes à croiser avec les seeds (« coupe homme Nice ») : les zones de type ville, à défaut la
   * ville des établissements. Un département ou une région reste dans `areas` (contexte) mais ne
   * fait pas une requête (« coupe homme Alpes-Maritimes » ne se cherche pas). Vide = périmètre
   * national ou aucune ville connue : pas d'expansion locale.
   */
  local_cities: Type.Optional(Type.Array(Type.String())),
  /**
   * Départements des établissements du pro (« 06 », « 2A », « 974 »), tirés de leur code postal :
   * une requête qui vise ce département (« quad 06 ») reste dans la zone, une requête qui en vise
   * un autre est écartée par la découverte (02/11 : « manucure russe 95 » pour un salon du 68).
   */
  area_departments: Type.Optional(Type.Array(Type.String())),
  /**
   * Sujets déjà validés ou rédigés par ville (`seo_keyword.area`) : la découverte fait tourner les
   * villes en commençant par la moins couverte et le scoring la favorise (rotation « coupe homme
   * Nice » ce mois-ci, « coupe homme Antibes » le suivant).
   */
  area_coverage: Type.Optional(Type.Array(Type.Object({ area: Type.String(), count: Type.Integer() }))),
  /** Langue/locale des contenus (fr-FR uniquement en v1). */
  locale: Type.Optional(Type.String()),
  page_index: Type.Array(SeoPageIndexEntrySchema),
  /** Chemins des pages de conversion (réserver, tarifs…). */
  conversion_paths: Type.Array(Type.String()),
  /** Appel à l'action par défaut (objectif + URL). */
  cta: Type.Object({
    goal: Type.String(),
    url: Type.Optional(Type.String()),
  }),
  /** Faits autorisés dans tous les articles de la cible. */
  facts: Type.Array(SeoProfileFactSchema),
  forbidden_terms: Type.Array(Type.String()),
  /** Hôtes vers lesquels un lien est interdit (concurrents). */
  forbidden_hosts: Type.Array(Type.String()),
  /**
   * Sites concurrents dont les sujets nourrissent la découverte : `hôte` ou `hôte/chemin`
   * (« fresha.com/fr/blog » = lire cette section), depuis les réglages « sites concurrents »,
   * complétés par la SERP « activité + ville ». Recherche autorisée, citation interdite : leurs
   * mots-clés deviennent des candidats `COMPETITOR`, leur nom n'entre jamais dans un article.
   */
  competitor_sites: Type.Optional(Type.Array(Type.String())),
  /**
   * Métier de la cible, pour chercher ses concurrents (« agence de communication » + ville) :
   * activités et sujets ciblés, les prestations non génériques à défaut (`competitorSeedsOf`). Les
   * noms de prestations (« Prise de contact », « Hébergement ») remontaient Carrefour Contact et les
   * hôtels de la ville (MEP du 20/10). Absent = `seeds` (Propelsia) ; vide = aucune recherche.
   */
  competitor_seeds: Type.Optional(Type.Array(Type.String())),
  /** Types de contenu autorisés pour la cible. */
  content_types: Type.Array(Type.Enum(SeoContentType)),
  /** Mémoire éditoriale : refus motivés, consignes du pro ou de l'équipe. */
  editorial_notes: Type.Array(Type.String()),
  /**
   * Sujets refusés par le pro avec son motif (tri des sujets) : la découverte écarte ce qui y
   * ressemble et vise à côté, au lieu de reproposer des variantes du même sujet.
   */
  rejected_topics: Type.Optional(Type.Array(Type.Object({ keyword: Type.String(), reason: Type.String() }))),
  /** Domaines hors champ (médical, allégations de résultat…), en clair pour le prompt. */
  out_of_scope: Type.Array(Type.String()),
  /** Sujets à ne jamais proposer (réglages) : mots courts, filtrés par sous-chaîne à la découverte et rappelés au classement. */
  excluded_topics: Type.Optional(Type.Array(Type.String())),
  /**
   * Interdits éditoriaux en phrases, saisis dans « sujets à ne jamais proposer » (réglages,
   * `seoSettingsEntries().instructions`) : rappelés entiers au classifieur de découverte, jamais
   * utilisés comme filtre par sous-chaîne. Ils figurent aussi, préfixés, dans `editorial_notes`.
   */
  excluded_topic_notes: Type.Optional(Type.Array(Type.String())),
});
export type SeoTargetProfile = Static<typeof SeoTargetProfileSchema>;
